import {Dispatch} from "redux";

import {IFetchContext} from "@pg-mono/data-fetcher";
import {appendQueryString, catch400, getRequest, IQueryValue} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiV2ListLink, Scenario} from "@pg-mono/rp-api-routes/";

import {setGlobals} from "../../../app/actions/globals";
import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {IPaginatedResponse} from "../../../request/IResponse";
import {offerDetailPropertiesListConstraints} from "../../constants/offer_detail_properties_list_constraints";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../constants/offer_detail_properties";
import {getOfferDetailPropertyListQueryFilters} from "../utils/getOfferDetailPropertyListQueryFilters";

const FETCH_OFFER_DETAIL_PROPERTIES = "offer_detail/FETCH_OFFER_DETAIL_PROPERTIES";
const latestClientRequestByList = new Map<string, symbol>();

export interface IPropertyListPropertyPlanImage {
    p_img_155x87: string;
    p_img_375x250: string;
    p_img_560: string;
    p_img_760x428: string;
    p_img_980: string;
    p_img_1500: string;
}

export interface IOfferDetailPropertiesResponse extends IPaginatedResponse {
    results: IPropertyListProperty[];
}

/**
 * Actions
 */
// offer detail properties
export const fetchOfferDetailPropertiesTypes = {
    filter: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/FILTER`),
    investment: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/INVESTMENT`),
    modal: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/MODAL`),
    promotion: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/PROMOTION`)
};
export const resetFetchOfferDetailProperties = {
    filter: () => ({type: fetchOfferDetailPropertiesTypes.filter.reset}),
    investment: () => ({type: fetchOfferDetailPropertiesTypes.investment.reset}),
    modal: () => ({type: fetchOfferDetailPropertiesTypes.modal.reset}),
    promotion: () => ({type: fetchOfferDetailPropertiesTypes.promotion.reset})
};

export const SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB = `${FETCH_OFFER_DETAIL_PROPERTIES}/SET_ACTIVE_TAB`;

export interface ISetOfferDetailPropertiesActiveTabAction {
    type: typeof SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB;
    activeTab: PropertyListTabTypes;
    [extraProps: string]: unknown;
}

/**
 * Sets the active property-list tab explicitly. We intentionally drive the active tab through this action
 * instead of relying on `fetch*.start` so that preloading promotion properties (to know their count) does
 * not switch the visible tab/UI.
 */
export const setOfferDetailPropertiesActiveTab = (activeTab: PropertyListTabTypes): ISetOfferDetailPropertiesActiveTabAction => ({
    type: SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB,
    activeTab
});

export const fetchOfferDetailPropertiesAtRoute = (ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: Dispatch, getState: () => IRPStore) => {
    dispatch({type: fetchOfferDetailPropertiesTypes.filter.start});
    const {offer} = getState().offerDetail;
    const preferences = getState().user.preferences?.data?.filters;
    const offerType = getState().offerDetail.offer?.type;
    const propertyListFilters = getOfferDetailPropertyListQueryFilters(preferences, offerType);

    const url = appendQueryString(apiV2ListLink.property.list(Scenario.PROPERTY_LIST), {
        ...offerDetailPropertiesListConstraints,
        offer: offer?.id,
        ...propertyListFilters,
        ...(offerType ? {type: offerType} : {}),
        page_size: OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
        page: 1
    });
    return getRequest(ctx.meta, url).then((json: IOfferDetailPropertiesResponse) => {
        const result = {
            properties: json.results,
            count: json.count
        };

        dispatch(setGlobals({hasPageProjection2d: json.results.some((p) => p.external_plan_url)}));
        dispatch({type: fetchOfferDetailPropertiesTypes.filter.success, result});
        return json;
    });
};

export enum PropertyListTabTypes {
    INVESTMENT = "investment",
    FILTER = "filter",
    MODAL = "modal",
    PROMOTION = "promotion"
}

export interface IFetchOfferDetailPropertiesQueryValues {
    page?: number;
    sort?: string | null;
}

export const fetchOfferDetailProperties =
    (
        offerId: number,
        tab: PropertyListTabTypes,
        page_size: number,
        queryValues?: IFetchOfferDetailPropertiesQueryValues,
        filterValues: Record<string, IQueryValue> = {}
    ) =>
    async (dispatch: Dispatch): Promise<IPropertyListProperty[] | void> => {
        const requestKey = `${tab}:${offerId}`;
        const requestToken = Symbol(requestKey);
        latestClientRequestByList.set(requestKey, requestToken);
        dispatch({type: fetchOfferDetailPropertiesTypes[tab].start, offerId});

        const sort = (queryValues && queryValues.sort) || undefined;
        const page = (queryValues && queryValues.page) || 1;

        const url = appendQueryString(apiV2ListLink.property.list(Scenario.PROPERTY_LIST), {
            ...(tab === PropertyListTabTypes.INVESTMENT ? {offer_with_properties_roi: offerId} : {offer: offerId}),
            ...(tab === PropertyListTabTypes.PROMOTION ? {is_promo: true} : {}),
            ...filterValues,
            page_size,
            page,
            sort,
            ...offerDetailPropertiesListConstraints
        });

        return getRequest({}, url)
            .then((json: IOfferDetailPropertiesResponse) => {
                if (latestClientRequestByList.get(requestKey) !== requestToken) {
                    return;
                }

                const result = {
                    properties: json.results,
                    count: json.count,
                    page_size: json.page_size || 20,
                    page: json.page || 1
                };
                dispatch({type: fetchOfferDetailPropertiesTypes[tab].success, result, offerId});
                return json.results;
            })
            .catch(
                catch400((err) => {
                    if (latestClientRequestByList.get(requestKey) === requestToken) {
                        dispatch({type: fetchOfferDetailPropertiesTypes[tab].error, err, offerId});
                    }
                })
            )
            .finally(() => {
                if (latestClientRequestByList.get(requestKey) === requestToken) {
                    latestClientRequestByList.delete(requestKey);
                }
            });
    };
import {Dispatch} from "redux";

import {IFetchContext} from "@pg-mono/data-fetcher";
import {appendQueryString, catch400, getRequest, IQueryValue} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiV2ListLink, Scenario} from "@pg-mono/rp-api-routes/";

import {setGlobals} from "../../../app/actions/globals";
import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {IPaginatedResponse} from "../../../request/IResponse";
import {offerDetailPropertiesListConstraints} from "../../constants/offer_detail_properties_list_constraints";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../constants/offer_detail_properties";
import {getOfferDetailPropertyListQueryFilters} from "../utils/getOfferDetailPropertyListQueryFilters";

const FETCH_OFFER_DETAIL_PROPERTIES = "offer_detail/FETCH_OFFER_DETAIL_PROPERTIES";
const latestClientRequestByList = new Map<string, symbol>();

export interface IPropertyListPropertyPlanImage {
    p_img_155x87: string;
    p_img_375x250: string;
    p_img_560: string;
    p_img_760x428: string;
    p_img_980: string;
    p_img_1500: string;
}

export interface IOfferDetailPropertiesResponse extends IPaginatedResponse {
    results: IPropertyListProperty[];
}

/**
 * Actions
 */
// offer detail properties
export const fetchOfferDetailPropertiesTypes = {
    filter: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/FILTER`),
    investment: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/INVESTMENT`),
    modal: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/MODAL`),
    promotion: createRequestActionTypes(`${FETCH_OFFER_DETAIL_PROPERTIES}/PROMOTION`)
};
export const resetFetchOfferDetailProperties = {
    filter: () => ({type: fetchOfferDetailPropertiesTypes.filter.reset}),
    investment: () => ({type: fetchOfferDetailPropertiesTypes.investment.reset}),
    modal: () => ({type: fetchOfferDetailPropertiesTypes.modal.reset}),
    promotion: () => ({type: fetchOfferDetailPropertiesTypes.promotion.reset})
};

export const SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB = `${FETCH_OFFER_DETAIL_PROPERTIES}/SET_ACTIVE_TAB`;

export interface ISetOfferDetailPropertiesActiveTabAction {
    type: typeof SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB;
    activeTab: PropertyListTabTypes;
    [extraProps: string]: unknown;
}

/**
 * Sets the active property-list tab explicitly. We intentionally drive the active tab through this action
 * instead of relying on `fetch*.start` so that preloading promotion properties (to know their count) does
 * not switch the visible tab/UI.
 */
export const setOfferDetailPropertiesActiveTab = (activeTab: PropertyListTabTypes): ISetOfferDetailPropertiesActiveTabAction => ({
    type: SET_OFFER_DETAIL_PROPERTIES_ACTIVE_TAB,
    activeTab
});

export const fetchOfferDetailPropertiesAtRoute = (ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: Dispatch, getState: () => IRPStore) => {
    dispatch({type: fetchOfferDetailPropertiesTypes.filter.start});
    const {offer} = getState().offerDetail;
    const preferences = getState().user.preferences?.data?.filters;
    const offerType = getState().offerDetail.offer?.type;
    const propertyListFilters = getOfferDetailPropertyListQueryFilters(preferences, offerType);

    const url = appendQueryString(apiV2ListLink.property.list(Scenario.PROPERTY_LIST), {
        ...offerDetailPropertiesListConstraints,
        offer: offer?.id,
        ...propertyListFilters,
        ...(offerType ? {type: offerType} : {}),
        page_size: OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
        page: 1
    });
    return getRequest(ctx.meta, url).then((json: IOfferDetailPropertiesResponse) => {
        const result = {
            properties: json.results,
            count: json.count
        };

        dispatch(setGlobals({hasPageProjection2d: json.results.some((p) => p.external_plan_url)}));
        dispatch({type: fetchOfferDetailPropertiesTypes.filter.success, result});
        return json;
    });
};

export enum PropertyListTabTypes {
    INVESTMENT = "investment",
    FILTER = "filter",
    MODAL = "modal",
    PROMOTION = "promotion"
}

export interface IFetchOfferDetailPropertiesQueryValues {
    page?: number;
    sort?: string | null;
}

export const fetchOfferDetailProperties =
    (
        offerId: number,
        tab: PropertyListTabTypes,
        page_size: number,
        queryValues?: IFetchOfferDetailPropertiesQueryValues,
        filterValues: Record<string, IQueryValue> = {}
    ) =>
    async (dispatch: Dispatch): Promise<IPropertyListProperty[] | void> => {
        const requestKey = `${tab}:${offerId}`;
        const requestToken = Symbol(requestKey);
        latestClientRequestByList.set(requestKey, requestToken);
        dispatch({type: fetchOfferDetailPropertiesTypes[tab].start, offerId});

        const sort = (queryValues && queryValues.sort) || undefined;
        const page = (queryValues && queryValues.page) || 1;

        const url = appendQueryString(apiV2ListLink.property.list(Scenario.PROPERTY_LIST), {
            ...(tab === PropertyListTabTypes.INVESTMENT ? {offer_with_properties_roi: offerId} : {offer: offerId}),
            ...(tab === PropertyListTabTypes.PROMOTION ? {is_promo: true} : {}),
            ...filterValues,
            page_size,
            page,
            sort,
            ...offerDetailPropertiesListConstraints
        });

        return getRequest({}, url)
            .then((json: IOfferDetailPropertiesResponse) => {
                if (latestClientRequestByList.get(requestKey) !== requestToken) {
                    return;
                }

                const result = {
                    properties: json.results,
                    count: json.count,
                    page_size: json.page_size || 20,
                    page: json.page || 1
                };
                dispatch({type: fetchOfferDetailPropertiesTypes[tab].success, result, offerId});
                return json.results;
            })
            .catch(
                catch400((err) => {
                    if (latestClientRequestByList.get(requestKey) === requestToken) {
                        dispatch({type: fetchOfferDetailPropertiesTypes[tab].error, err, offerId});
                    }
                })
            )
            .finally(() => {
                if (latestClientRequestByList.get(requestKey) === requestToken) {
                    latestClientRequestByList.delete(requestKey);
                }
            });
    };
